// src/activities/cape-v/capevAdaptations.js

export const CAPEV_ADAPTATIONS = Object.freeze({
  en: {
    label: "English (original)",
    i18nKey: "en",
    status: "originalInstrument"
  },
  es: {
    label: "Spanish",
    i18nKey: "es",
    status: "publishedAdaptation"
  },
  ca: {
    label: "Catalan",
    i18nKey: "ca",
    status: "publishedAdaptation"
  },
  fa: {
    label: "Farsi",
    i18nKey: "fa",
    status: "needsRightsReview"
  },
  fr: {
    label: "French (Europe)",
    i18nKey: "fr",
    status: "publishedAdaptation"
  },
  "fr-CA": {
    label: "French (Québec)",
    i18nKey: "fr-CA",
    status: "publishedAdaptation"
  },
  kn: {
    label: "Kannada",
    i18nKey: "kn",
    status: "needsRightsReview"
  },
  jp: {
    label: "Japanese",
    i18nKey: "jp",
    status: "publishedAdaptation"
  },
  my: {
    label: "Malay",
    i18nKey: "my",
    status: "publishedAdaptation"
  },
  "pt-br": {
    label: "Portuguese (Brazil)",
    i18nKey: "pt-BR",
    status: "publishedAdaptation"
  }
});

export const CAPEV_PUBLIC_ADAPTATION_IDS = [
  "en",
  "es",
  "ca",
  "fr",
  "fr-CA",
  "jp",
  "my",
  "pt-br"
];

const CAPEV_ADAPTATION_ALIASES = {
  "fr-ca": "fr-CA",
  "pt-BR": "pt-br",
  "pt_br": "pt-br",
  "pt_BR": "pt-br"
};

export function normalizeCAPEVAdaptationId(value) {
  if (!value) return null;

  const cleaned = String(value).trim();

  if (CAPEV_ADAPTATIONS[cleaned]) {
    return cleaned;
  }

  if (CAPEV_ADAPTATION_ALIASES[cleaned]) {
    return CAPEV_ADAPTATION_ALIASES[cleaned];
  }

  const lower = cleaned.toLowerCase();

  if (CAPEV_ADAPTATION_ALIASES[lower]) {
    return CAPEV_ADAPTATION_ALIASES[lower];
  }

  if (CAPEV_ADAPTATIONS[lower]) {
    return lower;
  }

  return null;
}

export function resolveCAPEVAdaptation(adaptationId, { fallbackId = "en" } = {}) {
  const normalizedId = normalizeCAPEVAdaptationId(adaptationId);
  const fallbackNormalizedId = normalizeCAPEVAdaptationId(fallbackId) || "en";
  const resolvedId =
    normalizedId && CAPEV_ADAPTATIONS[normalizedId]
      ? normalizedId
      : fallbackNormalizedId;

  return {
    id: resolvedId,
    ...CAPEV_ADAPTATIONS[resolvedId]
  };
}

export function getCAPEVAdaptationOptions(availableAdaptations = []) {
  const uniqueIds = new Set();

  return availableAdaptations
    .map(normalizeCAPEVAdaptationId)
    .filter((id) => {
      if (!id || uniqueIds.has(id) || !CAPEV_ADAPTATIONS[id]) {
        return false;
      }

      uniqueIds.add(id);
      return true;
    })
    .map((id) => ({
      id,
      ...CAPEV_ADAPTATIONS[id]
    }));
}

export function formatCAPEVAdaptationLabel(adaptationId) {
  const normalizedId = normalizeCAPEVAdaptationId(adaptationId);

  if (!normalizedId || !CAPEV_ADAPTATIONS[normalizedId]) {
    return null;
  }

  return CAPEV_ADAPTATIONS[normalizedId].label;
}

export function getCatalogEntryCAPEVInstrument(entry) {
  return entry?.metadata?.instrument || null;
}

export function isCatalogEntryCAPEV(entry) {
  const instrument = getCatalogEntryCAPEVInstrument(entry);

  return (
    instrument?.instrument === "CAPE-V" ||
    instrument?.baseInstrument === "CAPE-V"
  );
}

export function getCatalogEntryCAPEVAvailableAdaptations(entry) {
  const instrument = getCatalogEntryCAPEVInstrument(entry);
  const availableAdaptations = instrument?.availableAdaptations;

  if (Array.isArray(availableAdaptations)) {
    return availableAdaptations;
  }

  return [];
}

export function getDefaultCAPEVAdaptationId(entry) {
  const instrument = getCatalogEntryCAPEVInstrument(entry);
  const availableAdaptations =
    getCatalogEntryCAPEVAvailableAdaptations(entry);
  const defaultAdaptationId =
    normalizeCAPEVAdaptationId(instrument?.defaultAdaptationId) ||
    normalizeCAPEVAdaptationId(availableAdaptations[0]) ||
    "en";

  return resolveCAPEVAdaptation(defaultAdaptationId).id;
}

export function shouldRenderCAPEVAdaptationSelector(entry) {
  if (!isCatalogEntryCAPEV(entry)) {
    return false;
  }

  return getCAPEVAdaptationOptions(
    getCatalogEntryCAPEVAvailableAdaptations(entry)
  ).length > 1;
}
